import {PayloadAction, createSlice} from '@reduxjs/toolkit'
import {
  EmailState,
  FetchEmailsResponse,
  FetchEmailByIdResponse,
  FetchEmailStatsResponse,
} from './types'
import {
  fetchEmailById,
  fetchEmails,
  fetchEmailStats,
  resendEmail,
  deleteEmail,
} from './service'

const initialState: EmailState = {
  emailForView: null,
  emails: [],
  total: 0,
  totalPages: 0,
  stats: null,
  listLoading: false,
  actionLoading: false,
  error: null,
}

const emailSlice = createSlice({
  name: 'email',
  initialState,
  reducers: {
    resetEmails: (state: EmailState) => {
      state.emails = []
      state.total = 0
      state.totalPages = 0
    },
    resetEmailStats: (state: EmailState) => {
      state.stats = null
    },
  },
  extraReducers: (builder) => {
    // Fetch Emails
    builder.addCase(fetchEmails.pending, (state: EmailState) => {
      state.listLoading = true
      state.error = null
    })
    builder.addCase(fetchEmails.rejected, (state: EmailState, action: PayloadAction<any>) => {
      const {error} = action.payload
      state.error = error
      state.listLoading = false
    })
    builder.addCase(
      fetchEmails.fulfilled,
      (state: EmailState, action: PayloadAction<FetchEmailsResponse>) => {
        state.listLoading = false
        state.error = null
        const {emails, total, totalPages} = action.payload
        state.emails = emails
        state.total = total
        state.totalPages = totalPages
      }
    )

    // Fetch Email By ID
    builder.addCase(fetchEmailById.pending, (state: EmailState) => {
      state.actionLoading = true
      state.error = null
    })
    builder.addCase(fetchEmailById.rejected, (state: EmailState, action: PayloadAction<any>) => {
      const {error} = action.payload
      state.error = error
      state.actionLoading = false
    })
    builder.addCase(
      fetchEmailById.fulfilled,
      (state: EmailState, action: PayloadAction<FetchEmailByIdResponse>) => {
        state.actionLoading = false
        state.error = null
        const {email} = action.payload
        state.emailForView = email
      }
    )

    // Fetch Email Stats
    builder.addCase(fetchEmailStats.pending, (state: EmailState) => {
      state.actionLoading = true
      state.error = null
    })
    builder.addCase(fetchEmailStats.rejected, (state: EmailState, action: PayloadAction<any>) => {
      const {error} = action.payload
      state.error = error
      state.actionLoading = false
    })
    builder.addCase(
      fetchEmailStats.fulfilled,
      (state: EmailState, action: PayloadAction<FetchEmailStatsResponse>) => {
        state.actionLoading = false
        state.error = null
        const {stats} = action.payload
        state.stats = stats
      }
    )

    // Resend Email
    builder.addCase(resendEmail.pending, (state: EmailState) => {
      state.actionLoading = true
      state.error = null
    })
    builder.addCase(resendEmail.rejected, (state: EmailState, action: PayloadAction<any>) => {
      const {error} = action.payload
      state.error = error
      state.actionLoading = false
    })
    builder.addCase(resendEmail.fulfilled, (state: EmailState, action: PayloadAction<any>) => {
      state.actionLoading = false
      state.error = null
      const {email} = action.payload
      const index = state.emails.findIndex((e) => e._id === email._id)
      if (index !== -1) {
        state.emails[index] = email
      }
    })

    // Delete Email
    builder.addCase(deleteEmail.pending, (state: EmailState) => {
      state.actionLoading = true
      state.error = null
    })
    builder.addCase(deleteEmail.rejected, (state: EmailState, action: PayloadAction<any>) => {
      const {error} = action.payload
      state.error = error
      state.actionLoading = false
    })
    builder.addCase(deleteEmail.fulfilled, (state: EmailState, action: PayloadAction<any>) => {
      state.actionLoading = false
      state.error = null
      const {email} = action.payload
      state.emails = state.emails.filter((e) => e._id !== email._id)
      state.total -= 1
    })
  },
})

export const {resetEmails, resetEmailStats} = emailSlice.actions
export default emailSlice.reducer

